import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useRejectLicense } from '@utils/api/back-office/licensing';
import { AdminLicenseRequest } from '@utils/types/back-office/licensing';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  request: AdminLicenseRequest | null;
}

interface FormData {
  reason: string;
}

export default function RejectRequestModal({
  isOpen,
  onClose,
  request
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const rejectLicense = useRejectLicense();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object().shape({
    reason: Yup.string()
      .min(1, t('reason-required'))
      .max(2000, t('max-2000-characters'))
      .required(t('reason-required'))
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      reason: ''
    }
  });

  const onSubmit = async (data: FormData) => {
    if (!request) return;

    setIsSubmitting(true);
    try {
      await rejectLicense.mutateAsync({
        requestId: request.id,
        payload: { reason: data.reason }
      });
      handleClose();
    } catch {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!request) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="small"
      title={t('reject-request')}
      caption={t('reject-license-request-for', {
        org: request.target_org_name
      })}
      footerActions={[
        <Button
          key="cancel"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="reject"
          onClick={handleSubmit(onSubmit)}
          label={isSubmitting ? t('rejecting') : t('reject')}
          size="small"
          variant="danger"
          disabled={isSubmitting || !isValid}
        />
      ]}
    >
      <form className="space-y-4">
        {/* Request Info */}
        <div className="p-3 rounded-2xl border border-hairline bg-app text-sm">
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('request-id')}:
            </span>{' '}
            #{request.id}
          </p>
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('limits')}:
            </span>{' '}
            {request.org_limit} {t('orgs')} / {request.user_limit} {t('users')}
          </p>
        </div>

        {/* Reason - required */}
        <Input
          id="reason"
          label={
            <>
              {t('rejection-reason')} <span className="text-red-500">*</span>
            </>
          }
          multiline
          size="small"
          {...register('reason')}
          error={errors.reason?.message}
          placeholder={t('enter-rejection-reason')}
        />
        <p
          className="text-xs -mt-2"
          style={{ color: getColour('inactiveText') }}
        >
          {t('rejection-reason-visible-to-reseller')}
        </p>
      </form>
    </Modal>
  );
}
